import { useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarEvent } from '../types/todo';
import { queryKeys } from '../lib/queryKeys';
import { useAuth } from '../context/AuthContext';
import {
  fetchGcalStatus,
  fetchGcalAuthUrl,
  fetchGcalEvents,
  disconnectGcal,
  createGcalEvent,
  deleteGcalEvent,
  updateGcalEvent,
  CreateEventPayload,
} from '../services/googleCalendarService';

const GOOGLE_CALENDAR_ENABLED = process.env.REACT_APP_ENABLE_GOOGLE_CALENDAR === 'true';
const STALE = 5 * 60 * 1000;

export function useGoogleCalendar(date: string) {
  const { userId } = useAuth();
  const qc = useQueryClient();
  const statusKey = queryKeys.gcalStatus(userId);
  const eventsKey = queryKeys.gcalEvents(userId, date);

  // ── Status query ──────────────────────────────────────────────────────────
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: statusKey,
    queryFn: fetchGcalStatus,
    staleTime: STALE,
    enabled: GOOGLE_CALENDAR_ENABLED && !!userId,
  });
  const connected = statusData?.connected ?? false;

  // ── Events query ──────────────────────────────────────────────────────────
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: eventsKey,
    queryFn: () => fetchGcalEvents(date),
    staleTime: STALE,
    enabled: GOOGLE_CALENDAR_ENABLED && !!userId && connected,
  });
  const events: CalendarEvent[] = eventsData?.events ?? [];

  // ── Connect (opens popup, then polls status) ─────────────────────────────
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const connect = useCallback(async () => {
    if (!GOOGLE_CALENDAR_ENABLED) return;
    try {
      const data = await fetchGcalAuthUrl();
      window.open(data.url, 'google-auth', 'width=500,height=600');
      pollRef.current = setInterval(async () => {
        try {
          const status = await fetchGcalStatus();
          if (status.connected) {
            clearInterval(pollRef.current!);
            qc.setQueryData(statusKey, { connected: true });
          }
        } catch { /* ignore */ }
      }, 2000);
      setTimeout(() => {
        if (pollRef.current) clearInterval(pollRef.current);
      }, 300_000);
    } catch (err) {
      console.error('Failed to start Google auth:', err);
    }
  }, [qc, statusKey]);

  // ── Disconnect ────────────────────────────────────────────────────────────
  const disconnectMutation = useMutation({
    mutationFn: disconnectGcal,
    onSuccess: () => {
      qc.setQueryData(statusKey, { connected: false });
      qc.removeQueries({ queryKey: ['gcal-events', userId] });
    },
  });
  const disconnect = useCallback(() => disconnectMutation.mutate(), [disconnectMutation]);

  // ── Create event ─────────────────────────────────────────────────────────
  const createEvent = useMutation({
    mutationFn: (payload: CreateEventPayload) => createGcalEvent(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventsKey }),
  });

  // ── Delete event ─────────────────────────────────────────────────────────
  const deleteEvent = useMutation({
    mutationFn: (eventId: string) => deleteGcalEvent(eventId),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventsKey }),
  });

  // ── Update event ─────────────────────────────────────────────────────────
  const updateEvent = useMutation({
    mutationFn: ({ eventId, patch }: { eventId: string; patch: Partial<{ title: string; startTime: string; endTime: string }> }) =>
      updateGcalEvent(eventId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventsKey }),
  });

  return {
    enabled: GOOGLE_CALENDAR_ENABLED,
    connected,
    events,
    loading: statusLoading || eventsLoading,
    connect,
    disconnect,
    createEvent,
    deleteEvent,
    updateEvent,
  };
}

